import { PROJECTS, SERVICES } from "@/data";

export const SITE_URL = "https://codelank.com";
export const SITE_NAME = "CODELANK";
export const OG_IMAGE = `${SITE_URL}/og-image.png`;
const JOB_TITLE = "AI-Forward Full Stack Developer";
const HOME_DESC = "AI-forward full stack development, secure AI applications, business automation, SaaS platforms and custom software built by CODELANK.";

const trim = (s, n = 155) => (s.length <= n ? s : `${s.slice(0, n - 1).replace(/\s+\S*$/, "")}…`).replace("…", "...");
const norm = (p) => (p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p);

const PERSON = { "@type": "Person", "@id": `${SITE_URL}/#person`, name: SITE_NAME, jobTitle: JOB_TITLE, url: SITE_URL };
const ORG = {
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  image: OG_IMAGE,
  description: HOME_DESC,
  founder: { "@id": PERSON["@id"] },
  knowsAbout: ["Full stack development", "AI integration", "Retrieval-augmented generation", "SaaS platforms", "Dashboards", "Mobile apps"],
  makesOffer: SERVICES.map((s) => ({
    "@type": "Offer",
    url: `${SITE_URL}/services/${s.slug}`,
    price: s.price,
    priceCurrency: "USD",
    itemOffered: { "@type": "Service", name: s.name, description: s.desc },
  })),
};
const WEBSITE = { "@type": "WebSite", "@id": `${SITE_URL}/#website`, name: SITE_NAME, url: SITE_URL, description: HOME_DESC, publisher: { "@id": ORG["@id"] } };

const crumbs = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE_URL}${path}` })),
});

const page = (path, title, description, extra = {}) => ({
  path,
  title,
  description,
  canonical: `${SITE_URL}${path === "/" ? "/" : path}`,
  ogType: "website",
  image: OG_IMAGE,
  robots: "index, follow, max-image-preview:large",
  ...extra,
});

const STATIC = {
  "/": page("/", "CODELANK | AI-Forward Full Stack Developer", HOME_DESC, { jsonLd: { "@context": "https://schema.org", "@graph": [WEBSITE, ORG, PERSON] } }),
  "/work": page("/work", "Work | Shipped Apps and Case Studies | CODELANK", "Five shipped products: Tune4Tune, Freight KPI, ExecLens, PurseImburse and Mira. Marketplaces, dashboards and AI apps built by CODELANK.", {
    jsonLd: { "@context": "https://schema.org", "@graph": [{ "@type": "CollectionPage", name: "Work", url: `${SITE_URL}/work`, isPartOf: { "@id": WEBSITE["@id"] } }, crumbs([["Home", "/"], ["Work", "/work"]])] },
  }),
  "/services": page("/services", "Services and Pricing | Web Apps, AI, SaaS | CODELANK", "Landing pages from $500, websites from $800, AI integration from $500, dashboards from $1,000, web apps from $1,200, mobile apps from $1,800, SaaS from $10,000.", {
    jsonLd: { "@context": "https://schema.org", "@graph": [{ "@type": "CollectionPage", name: "Services", url: `${SITE_URL}/services`, isPartOf: { "@id": WEBSITE["@id"] } }, crumbs([["Home", "/"], ["Services", "/services"]])] },
  }),
  "/about": page("/about", "About | How CODELANK Builds Software", "One senior AI-forward full stack developer. How CODELANK started, how projects run, the stack used and why most builds ship in under three weeks.", {
    ogType: "profile",
    jsonLd: { "@context": "https://schema.org", "@graph": [{ "@type": "AboutPage", name: "About CODELANK", url: `${SITE_URL}/about`, about: { "@id": ORG["@id"] } }, crumbs([["Home", "/"], ["About", "/about"]])] },
  }),
  "/security": page("/security", "Security Practices | RAG, Escrow, Access Control | CODELANK", "How CODELANK keeps client data and money safe: RAG boundaries for AI, escrow payments, role-based access, HTTP-only sessions and signature-verified webhooks.", {
    jsonLd: { "@context": "https://schema.org", "@graph": [{ "@type": "WebPage", name: "Security practices", url: `${SITE_URL}/security`, isPartOf: { "@id": WEBSITE["@id"] } }, crumbs([["Home", "/"], ["Security", "/security"]])] },
  }),
  "/contact": page("/contact", "Contact | Start a Project with CODELANK", "Send a project brief and get a quote within 24 hours. No payment due up front. Web apps, AI integration, dashboards, mobile apps and SaaS.", {
    jsonLd: { "@context": "https://schema.org", "@graph": [{ "@type": "ContactPage", name: "Contact CODELANK", url: `${SITE_URL}/contact`, isPartOf: { "@id": WEBSITE["@id"] } }, crumbs([["Home", "/"], ["Contact", "/contact"]])] },
  }),
};

const PRIVATE = ["/admin", "/pay", "/payment"];

export const NOT_FOUND_SEO = { title: "Page not found | CODELANK", description: "This page does not exist.", robots: "noindex, follow", canonical: null, image: OG_IMAGE, ogType: "website" };

export function getSeo(pathname) {
  const p = norm(pathname || "/");
  if (STATIC[p]) return STATIC[p];
  if (PRIVATE.some((x) => p === x || p.startsWith(`${x}/`))) {
    return { title: "CODELANK", description: "", robots: "noindex, nofollow", canonical: null, image: OG_IMAGE, ogType: "website" };
  }
  const work = p.match(/^\/work\/([a-z0-9-]+)$/);
  if (work) {
    const pr = PROJECTS.find((x) => x.slug === work[1]);
    if (pr) {
      return page(p, `${pr.title} | ${pr.subtitle} | CODELANK Work`, trim(pr.desc), {
        ogType: "article",
        image: `${SITE_URL}${pr.image}`,
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "SoftwareApplication", name: pr.title, alternateName: pr.subtitle, description: pr.desc, applicationCategory: pr.category, image: `${SITE_URL}${pr.image}`, url: pr.live || `${SITE_URL}${p}`, operatingSystem: "Web", creator: { "@id": ORG["@id"] } },
            crumbs([["Home", "/"], ["Work", "/work"], [pr.title, p]]),
          ],
        },
      });
    }
  }
  const svc = p.match(/^\/services\/([a-z0-9-]+)$/);
  if (svc) {
    const s = SERVICES.find((x) => x.slug === svc[1]);
    if (s) {
      return page(p, `${s.name} Development from $${s.price.toLocaleString()} | CODELANK`, trim(`${s.desc} Starting at $${s.price.toLocaleString()}, about ${s.weeks} ${s.weeks === 1 ? "week" : "weeks"} to deliver.`), {
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Service", name: s.name, description: s.desc, serviceType: s.name, provider: { "@id": ORG["@id"] }, url: `${SITE_URL}${p}`, offers: { "@type": "Offer", price: s.price, priceCurrency: "USD", url: `${SITE_URL}${p}` } },
            crumbs([["Home", "/"], ["Services", "/services"], [s.name, p]]),
          ],
        },
      });
    }
  }
  return NOT_FOUND_SEO;
}

export const publicRoutes = () => [
  ...Object.keys(STATIC),
  ...PROJECTS.map((p) => `/work/${p.slug}`),
  ...SERVICES.map((s) => `/services/${s.slug}`),
];

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

export function headHtml(seo) {
  const tags = [
    `<meta name="robots" content="${esc(seo.robots)}"/>`,
    seo.canonical ? `<link rel="canonical" href="${esc(seo.canonical)}"/>` : "",
    `<meta property="og:site_name" content="${SITE_NAME}"/>`,
    `<meta property="og:type" content="${esc(seo.ogType)}"/>`,
    `<meta property="og:title" content="${esc(seo.title)}"/>`,
    `<meta property="og:description" content="${esc(seo.description)}"/>`,
    seo.canonical ? `<meta property="og:url" content="${esc(seo.canonical)}"/>` : "",
    `<meta property="og:image" content="${esc(seo.image)}"/>`,
    `<meta property="og:image:alt" content="CODELANK, AI-Forward Full Stack Developer"/>`,
    `<meta property="og:locale" content="en_US"/>`,
    `<meta name="twitter:card" content="summary_large_image"/>`,
    `<meta name="twitter:title" content="${esc(seo.title)}"/>`,
    `<meta name="twitter:description" content="${esc(seo.description)}"/>`,
    `<meta name="twitter:image" content="${esc(seo.image)}"/>`,
    seo.jsonLd ? `<script type="application/ld+json" id="ld-json">${JSON.stringify(seo.jsonLd).replace(/</g, "\\u003c")}</script>` : "",
  ];
  return tags.filter(Boolean).join("");
}

function upsert(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  if (!value) { if (el) el.remove(); return; }
  if (!el) { el = create(); document.head.appendChild(el); }
  el.setAttribute(attr, value);
}
const meta = (key, isProp) => () => { const m = document.createElement("meta"); m.setAttribute(isProp ? "property" : "name", key); return m; };

export function applySeo(seo) {
  document.title = seo.title;
  upsert('meta[name="description"]', meta("description"), "content", seo.description);
  upsert('meta[name="robots"]', meta("robots"), "content", seo.robots);
  upsert('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; }, "href", seo.canonical);
  const og = { "og:site_name": SITE_NAME, "og:type": seo.ogType, "og:title": seo.title, "og:description": seo.description, "og:url": seo.canonical, "og:image": seo.image };
  Object.entries(og).forEach(([k, v]) => upsert(`meta[property="${k}"]`, meta(k, true), "content", v));
  const tw = { "twitter:card": "summary_large_image", "twitter:title": seo.title, "twitter:description": seo.description, "twitter:image": seo.image };
  Object.entries(tw).forEach(([k, v]) => upsert(`meta[name="${k}"]`, meta(k), "content", v));
  let ld = document.getElementById("ld-json");
  if (!seo.jsonLd) { if (ld) ld.remove(); return; }
  if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "ld-json"; document.head.appendChild(ld); }
  ld.textContent = JSON.stringify(seo.jsonLd);
}
